'use client';

import React, { forwardRef, useId } from 'react';
import classNames from 'classnames';
import { Caption, Text } from '@/design-system/components/Basic/Typography';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label?: string;
  error?: React.ReactNode | string;
  labelClassName?: string;
  className?: string;
  wrapperClassName?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      labelClassName,
      className,
      wrapperClassName,
      id,
      required,
      disabled,
      icon,
      iconPosition = 'right',
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? `input-${generatedId.replace(/:/g, '-')}`;

    return (
      <div className={classNames('ds-input-wrapper flex flex-col gap-3', wrapperClassName)}>
        {label && (
          <Text as="label" size="s" weight="semibold" htmlFor={inputId} className={labelClassName}>
            {label}
            {required && ' *'}
          </Text>
        )}
        <div
          className={classNames('container-gradient-border rounded-xl', {
            'relative flex items-center gap-2 ': !!icon,
          })}
        >
          {icon && iconPosition === 'left' && (
            <span className="pointer-events-none shrink-0">{icon}</span>
          )}
          <input
            ref={ref}
            id={inputId}
            required={required}
            disabled={disabled}
            placeholder=" "
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
            className={classNames(
              'ds-input-trigger-base input-border-gradient min-h-ds-11 px-3.5 py-2.5',
              className,
              {
                'pl-10': !!icon && iconPosition === 'left',
                'pr-10': !!icon && iconPosition === 'right',
              },
            )}
            {...rest}
          />
          {icon && iconPosition === 'right' && (
            <span className="pointer-events-none absolute right-4 shrink-0">{icon}</span>
          )}
        </div>
        {error && (
          <Caption as="p" className="text-ds-text-feedback-error">
            {error}
          </Caption>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
